import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Users, PiggyBank, HandCoins, CalendarCheck,
  FileSpreadsheet, Settings, LogOut, Menu, X, Database, Banknote,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-admin-dashboard" },
  { to: "/admin/members", label: "Anggota", icon: Users, testid: "nav-admin-members" },
  { to: "/admin/simpanan", label: "Simpanan", icon: PiggyBank, testid: "nav-admin-simpanan" },
  { to: "/admin/management-utang", label: "Management Utang", icon: Banknote, testid: "nav-admin-management-utang" },
  { to: "/admin/pinjaman", label: "Pinjaman", icon: HandCoins, testid: "nav-admin-pinjaman" },
  { to: "/admin/angsuran", label: "Angsuran", icon: CalendarCheck, testid: "nav-admin-angsuran" },
  { to: "/admin/master-data", label: "Master Data", icon: Database, testid: "nav-admin-master-data" },
  { to: "/admin/reports", label: "Laporan", icon: FileSpreadsheet, testid: "nav-admin-reports" },
  { to: "/admin/config", label: "Konfigurasi", icon: Settings, testid: "nav-admin-config" },
];

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { config, logout, user } = useAuth();
  const [open, setOpen] = useState(false);

  const isActive = (item) => (item.end ? loc.pathname === item.to : loc.pathname.startsWith(item.to));

  const Brand = (
    <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
      {config?.logo ? (
        <img src={config.logo} alt="logo" className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/20" />
      ) : (
        <div className="w-10 h-10 rounded-xl bg-gold flex items-center justify-center font-heading font-bold text-brand">
          K
        </div>
      )}
      <div className="leading-tight">
        <div className="font-heading font-bold text-white text-base">{config?.name || "Kopsebra"}</div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">Admin Panel</div>
      </div>
    </div>
  );

  const NavList = (
    <nav className="px-3 py-4 space-y-1">
      <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Menu</div>
      {NAV.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={item.testid}
            className={
              "group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition " +
              (active
                ? "bg-white/15 text-white shadow-inner ring-1 ring-white/10"
                : "text-white/75 hover:bg-white/10 hover:text-white")
            }
          >
            <span className={"w-8 h-8 rounded-lg flex items-center justify-center " + (active ? "bg-gold/90 text-brand" : "bg-white/5 text-white/80 group-hover:bg-white/10")}>
              <Icon size={16} />
            </span>
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-mint-pattern">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-brand text-white sticky top-0 h-screen overflow-y-auto">
        {Brand}
        {NavList}
        <div className="mt-auto p-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-2">Login sebagai</div>
          <div className="text-sm font-medium truncate" data-testid="admin-current-user">{user?.full_name}</div>
          <div className="text-xs text-white/50 truncate">{user?.email}</div>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => { await logout(); nav("/"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm py-2 transition"
          >
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-brand-ink/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-brand text-white flex flex-col">
            <button
              className="absolute right-3 top-3 p-2 rounded-full bg-white/10 text-white"
              onClick={() => setOpen(false)}
              aria-label="close"
              data-testid="admin-drawer-close"
            >
              <X size={16} />
            </button>
            {Brand}
            {NavList}
            <div className="mt-auto p-4 border-t border-white/10">
              <button
                data-testid="admin-logout-mobile-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm py-2 transition"
              >
                <LogOut size={14} /> Keluar
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-brand/10 px-4 sm:px-8 py-3 flex items-center gap-3">
          <button
            className="lg:hidden p-2 rounded-xl bg-brand-soft text-brand"
            onClick={() => setOpen(true)}
            aria-label="open menu"
            data-testid="admin-menu-btn"
          >
            <Menu size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand/60">Admin</div>
            <div className="font-heading font-bold text-brand-ink truncate">{config?.name || "Kopsebra"} — Sistem Manajemen Koperasi</div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-8 py-6 sm:py-10 max-w-7xl w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
